import { useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SignUpFormValues } from 'modules/sign-up/sign-up';
import { Field } from 'common/ui/field';
import MuiPhoneNumber from 'mui-phone-number';
import AddressInput from 'common/ui/address-input';
import { ClientType, TWhatAccountExist, UserConnectionType } from 'modules/sign-up/constants';
import toast from 'react-hot-toast';
import { ulid } from 'ulid';
import { initWithHash } from 'common/store/actions';
import { signUpActions } from 'modules/sign-up/store/actions';
import { SocialsSignup } from 'common/ui/socials-signup';
import { useParams } from 'react-router-dom';
import { CountrySelect } from 'common/ui/country-select';
import { BrandsHub } from 'common/constants/brands-hub';
import { ReactComponent as ArrowIcon } from 'common/svg/arrow.svg';
import { selectLoginUrl } from 'common/store/selectors';
import { ReactComponent as MavLogo } from 'common/svg/maverick.svg';
import { ExistingAccountNotice } from '../existing-account-notice';
import { StepIndicator } from '../step-indicator';

const getStyles = () => ({
  clientTypeButtonGroup: {
    mb: 3,
    border: 'none',
  },
  clientTypeButton: {
    width: 150,
    borderRadius: '28px',
    py: 1.2,
    fontSize: 16,
    fontWeight: 400,
    textTransform: 'none',
  },
  link: {
    textDecoration: 'underline',
    color: 'primary.main',
    cursor: 'pointer',
  },
  stepBox: (active: boolean) => ({
    width: 40,
    height: 6,
    borderRadius: 3,
    backgroundColor: active ? 'primary.main' : 'grey.300',
    transition: 'background 0.2s',
  }),
  termsLink: { fontSize: 14, cursor: 'pointer', textDecoration: 'underline' },
});

interface Props {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  openTermsModal: () => void;
  openServiceModal: () => void;
  setSocialError: React.Dispatch<React.SetStateAction<string | null>>;
  showSocialSignUpErrorDialog: (value: boolean) => void;
}

export function SignUpContent({
  step,
  setStep,
  openTermsModal,
  openServiceModal,
  setSocialError,
  showSocialSignUpErrorDialog,
}: Props): ReactElement {
  const { clientType = '' } = useParams();

  const dispatch = useDispatch();
  const { errors, getFieldMeta, values, setFieldValue, touched, setFieldTouched } =
    useFormikContext<SignUpFormValues>();
  const styles = getStyles();

  const [isAccountExist, setIsAccountExist] = useState(false);
  const [whatAccountExist, setWhatAccountExist] = useState<TWhatAccountExist>();
  const [selectedClientType, setSelectedClientType] = useState<string>(
    () => sessionStorage.getItem('selectedClientType') || clientType || ClientType.Brand
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [showManualSignUp, setShowManualSignUp] = useState<boolean>(false);

  const successfulUrl = useSelector(selectLoginUrl);
  const hash = sessionStorage.getItem('hash');

  const handleStepSuccess = () => {
    setStep((prev: number) => prev + 1);
    setLoading(false);
  };

  const handleStepError = (value: string) => {
    setLoading(false);
    toast.error(value);
  };

  const handleSocialError = (error: string) => {
    setSocialError(error);
    showSocialSignUpErrorDialog(true);
  };

  const handleWhatAccountExist = (errorMessage: string) => {
    switch (errorMessage) {
      case 'Account already exist':
        setWhatAccountExist(TWhatAccountExist.CARRIC_DB);
        setIsAccountExist(true);
        sessionStorage.removeItem('hash');
        break;
      case 'Email already exist':
        setWhatAccountExist(TWhatAccountExist.AOS_DB);
        setIsAccountExist(true);
        sessionStorage.removeItem('hash');
        break;
      default:
        toast.error(errorMessage);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFirstStep = (socialUser?: any) => {
    const isSocial = socialUser && typeof socialUser === 'object' && 'email' in socialUser && 'name' in socialUser;

    if (!isSocial) {
      const foundErrors = Object.entries(errors).filter(
        entry =>
          ['firstName', 'lastName', 'email', 'phone', 'password', 'confirmPassword'].includes(entry[0]) &&
          getFieldMeta(entry[0]).error
      );

      if (foundErrors.length) {
        toast.error('Enter required fields...');
        foundErrors.forEach(entry => setFieldTouched(entry[0]));
        return;
      }
    }

    setLoading(true);

    const email = isSocial ? socialUser.email : values.email;
    const hash = sessionStorage.getItem('hash') || ulid();
    sessionStorage.setItem('hash', hash);

    const data = isSocial
      ? {
          first_name: socialUser?.given_name || socialUser.name?.split(' ')[0] || '',
          last_name: socialUser?.family_name || socialUser.name?.split(' ')[1] || '',
          email: socialUser.email,
          phone: '',
          password: null as string | null,
          connection_type: UserConnectionType.Social,
          step: 'user_information',
        }
      : {
          first_name: values.firstName,
          last_name: values.lastName,
          phone: values.phone,
          email: values.email,
          password: values.password,
          connection_type: UserConnectionType.UsernamePassword,
          step: 'user_information',
        };

    dispatch(
      initWithHash(
        hash,
        email,
        BrandsHub.MaverickX,
        () => {
          dispatch(
            signUpActions.firstSignupStepAction(
              {
                data: {
                  ...data,
                  summary_service: values.summaryService,
                  terms_and_conditionals: values.termsAndConditionals,
                },
                hash,
              },
              () => {
                handleStepSuccess();
              },
              (errMsg: string) => {
                handleWhatAccountExist(errMsg);
              }
            )
          );
        },
        (errMsg: string) => {
          handleWhatAccountExist(errMsg);
        }
      )
    );
  };

  const handleSecondStep = () => {
    const foundErrors = Object.entries(errors).filter(
      entry => ['companyName', 'domain', 'address', 'country'].includes(entry[0]) && getFieldMeta(entry[0]).error
    );

    if (foundErrors.length) {
      toast.error('Enter required fields...');
      foundErrors.forEach(entry => setFieldTouched(entry[0]));
      return;
    }

    const mapCountry = (country: string) => {
      if (country === 'GB') return 'uk';
      if (country === 'US') return 'us';
      return country ? country.toLowerCase() : '';
    };

    const companyData = {
      company_name: values.companyName,
      domain: values.domain,
      country: mapCountry(values.country),
      is_affiliate_content: values.isAffiliateContent,
      is_brand: values.isBrand,
      address: values.address,
      ...(selectedClientType === 'creator' && { gainz_active: values.gainzActive }),
      step: 'company_information',
    };

    const additionalData = {
      ar_contact_first_name: values.arContactFirstName,
      ar_contact_last_name: values.arContactLastName,
      ar_contact_email: values.arContactEmail,

      acct_contact_first_name: values.acctContactFirstName,
      acct_contact_last_name: values.acctContactLastName,
      acct_contact_email: values.acctContactEmail,

      socials: {
        facebook: values.facebook !== '' ? values.facebook : null,
        instagram: values.instagram !== '' ? values.instagram : null,
        tiktok: values.tiktok !== '' ? values.tiktok : null,
        twitter: values.twitter !== '' ? values.twitter : null,
        youtube: values.youtube !== '' ? values.youtube : null,
      },

      step: 'additional_information',
    };

    dispatch(
      signUpActions.secondSignupStepAction(
        {
          postCompanyInformation: {
            data: companyData,
            hash,
          },
          postAdditionalInformation: {
            data: additionalData,
            hash,
          },
        },
        () => {
          dispatch(
            signUpActions.completeSingUp(
              {
                hash,
                data: {
                  is_brand: values.isBrand,
                  ...(selectedClientType === 'creator' && { gainz_active: values.gainzActive }),
                },
              },
              handleStepError,
              handleStepSuccess
            )
          );
        },
        errMsg => {
          handleStepError(errMsg);
        }
      )
    );
  };

  const handleOpenLogin = () => {
    const url =
      selectedClientType === 'creator' ? 'https://brand.prospected.ai/partner' : 'https://brand.prospected.ai/';
    window.open(url, '_blank');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onBlurHandle = (e: any) => {
    if (!e.target.value) return;
    const hash = sessionStorage.getItem('hash') || ulid();
    sessionStorage.setItem('hash', hash);

    const payload = {
      hash,
      data: {
        field_name: e.target.name,
        field_data: e.target.value,
      },
    };

    dispatch(signUpActions.postDraftFields(payload));
  };

  useEffect(() => {
    sessionStorage.setItem('selectedClientType', selectedClientType);
    setFieldValue('isBrand', selectedClientType === 'brand');
  }, [selectedClientType, setFieldValue]);

  if (isAccountExist) {
    return <ExistingAccountNotice whatAccountExist={whatAccountExist} handleOpenLogin={handleOpenLogin} />;
  }

  switch (step) {
    case 1:
      return (
        <>
          <Box my={3}>
            <a href="https://maverickx.io/" target="_blank" rel="noopener noreferrer">
              <MavLogo />
            </a>
          </Box>
          <StepIndicator step={step} />
          <ButtonGroup size="medium" variant="outlined" sx={styles.clientTypeButtonGroup}>
            <Button
              sx={styles.clientTypeButton}
              color="primary"
              variant={selectedClientType === 'brand' ? 'contained' : 'outlined'}
              key="brand"
              onClick={() => {
                setSelectedClientType('brand');
                setFieldValue('isBrand', true);
              }}
            >
              Brand
            </Button>
            <Button
              sx={styles.clientTypeButton}
              color="primary"
              variant={selectedClientType === 'creator' ? 'contained' : 'outlined'}
              key="publisher"
              onClick={() => {
                setSelectedClientType('creator');
                setFieldValue('isBrand', false);
              }}
            >
              Creator
            </Button>
          </ButtonGroup>
          {selectedClientType && (
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
              Join as a {selectedClientType[0].toLocaleUpperCase() + selectedClientType.slice(1)}
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Already have an account?{' '}
            <Typography variant="body1" component="span" sx={styles.link} onClick={handleOpenLogin}>
              Log in!
            </Typography>
          </Typography>
          <SocialsSignup onSocialSignup={user => handleFirstStep(user)} onError={handleSocialError} />
          <Stack direction="row" alignItems="center" my={2} width="100%">
            <Divider sx={{ flexGrow: 1 }} />
            <Typography sx={{ mx: 2 }} color="text.secondary">
              or
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Stack>
          {!showManualSignUp && (
            <Stack direction="row" justifyContent="center" alignItems="center">
              <Typography
                variant="body2"
                sx={{ ...styles.link, display: 'block', textAlign: 'center' }}
                onClick={() => setShowManualSignUp(true)}
              >
                Sign up with email
                <ArrowIcon width={12} height={12} />
              </Typography>
            </Stack>
          )}
          <Collapse in={showManualSignUp} timeout={600} unmountOnExit>
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="firstName"
                    component={TextField}
                    label="First Name"
                    fullWidth
                    size="small"
                    onBlurCapture={onBlurHandle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="lastName"
                    component={TextField}
                    label="Last Name"
                    fullWidth
                    size="small"
                    onBlurCapture={onBlurHandle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="email"
                    component={TextField}
                    label="Email Address"
                    placeholder="hello@gmail.com"
                    fullWidth
                    size="small"
                    onBlurCapture={onBlurHandle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MuiPhoneNumber
                    name="phone"
                    variant="outlined"
                    fullWidth
                    size="small"
                    defaultCountry="us"
                    value={values.phone}
                    onChange={value => setFieldValue('phone', value)}
                    onBlur={() => setFieldTouched('phone', true)}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="password"
                    component={TextField}
                    label="Password"
                    type="password"
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="confirmPassword"
                    component={TextField}
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
              <Button fullWidth variant="contained" onClick={handleFirstStep}>
                {loading ? <CircularProgress size={26} color="inherit" /> : 'Next'}
              </Button>
            </Box>
          </Collapse>

          <Typography variant="caption" color="text.secondary" align="center" mt={1}>
            By joining or signing in, you agree to our{' '}
            <Typography variant="body1" component="span" onClick={openTermsModal} sx={styles.termsLink}>
              Terms and Conditions.
            </Typography>{' '}
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center">
            View{' '}
            <Typography variant="body1" component="span" onClick={openServiceModal} sx={styles.termsLink}>
              Service Summary.
            </Typography>
          </Typography>
        </>
      );
    case 2:
      return (
        <>
          <StepIndicator step={step} />
          <Typography variant="subtitle1" sx={{ mb: 3, fontSize: 20, fontWeight: 600 }}>
            Please provide your information below
          </Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <Field
                name="companyName"
                component={TextField}
                label={`${selectedClientType[0].toLocaleUpperCase() + selectedClientType.slice(1)} Name`}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <CountrySelect
                value={values.country}
                onChange={val => setFieldValue('country', val)}
                onBlur={() => setFieldTouched('country', true)}
                error={errors.country}
                touched={touched.country}
              />
            </Grid>
            <Grid item xs={12}>
              <AddressInput
                name="address"
                placeholder="e.g. 705 Acme Dr"
                size="small"
                fullWidth
                onBlur={() => setFieldTouched('address', true)}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="domain"
                component={TextField}
                label={`${selectedClientType === 'brand' ? 'Amazon Store URL' : 'Website URL'}`}
                placeholder={`${selectedClientType === 'brand' ? 'Amazon Store URL (https://...)' : 'e.g. domain.com'}`}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
          <Divider sx={{ mb: 3, width: '100%' }}>Socials</Divider>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <Field
                name="facebook"
                component={OutlinedInput}
                endAdornment={<img src="assets/images/facebook.svg" alt="Facebook" width={24} height={24} />}
                placeholder="Facebook"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="instagram"
                component={OutlinedInput}
                endAdornment={<img src="assets/images/instagram.png" alt="Instagram" width={24} height={24} />}
                placeholder="Instagram"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="youtube"
                component={OutlinedInput}
                endAdornment={<img src="assets/images/youtube.svg" alt="YouTube" width={24} height={24} />}
                placeholder="Youtube"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="x"
                component={OutlinedInput}
                endAdornment={<img src="assets/images/x.svg" alt="X" width={24} height={24} />}
                placeholder="X"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="tiktok"
                component={OutlinedInput}
                endAdornment={<img src="assets/images/tiktok.svg" alt="TikTok" width={24} height={24} />}
                placeholder="TikTok"
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
          <Button fullWidth variant="contained" onClick={handleSecondStep}>
            {loading ? <CircularProgress size={26} color="inherit" /> : 'Finish'}
          </Button>
        </>
      );
    case 3:
      return (
        <Box>
          <StepIndicator step={step} />
          <Typography variant="h4" fontWeight="bold" textAlign="center" mb={3}>
            Welcome!
          </Typography>
          <Alert sx={{ boxSizing: 'border-box', mb: 3 }} variant="outlined" severity="success">
            Thank you for submitting your application. It has been received and is being reviewed by our team. This
            process should only take a couple of minutes.
          </Alert>
          <Typography variant="body1" textAlign="center">
            You can log in to your account{' '}
            <Typography
              component="span"
              style={styles.link}
              onClick={() => {
                window.open(successfulUrl, '_blank');
              }}
              color="primary.main"
            >
              here.
            </Typography>
          </Typography>
        </Box>
      );
    default:
      return null;
  }
}
